// Add debouncing utility
let fetchInProgress = false;
let fetchTimeout = null;
let isInitialized = false;
let debounceTimeout = null;
const DEBOUNCE_DELAY = 150; // ms to wait before processing updates

// Debounced cart update handler to prevent multiple simultaneous fetches
function debouncedCartUpdate() {
    // Clear any pending update
    if (debounceTimeout) {
        clearTimeout(debounceTimeout);
    }
    
    // Queue this update
    debounceTimeout = setTimeout(async () => {
        try {
            const freshCart = await getCart();
            if (freshCart) {
                updateCartItemCount(freshCart);
            }
        } catch (error) {
            // Silently handle errors during early initialization
        }
        debounceTimeout = null;
    }, DEBOUNCE_DELAY);
}

// Set up event listeners IMMEDIATELY to catch cart updates before initialization completes
(function setupEarlyListeners() {
    // Listen for custom events that might indicate cart changes
    const handleCartUpdated = debouncedCartUpdate;

    // Set up listeners immediately (before DOM is ready)
    if (typeof document !== 'undefined') {
        document.addEventListener('cart:updated', handleCartUpdated);
        document.addEventListener('cart:change', handleCartUpdated);
        document.addEventListener('cart:refresh', handleCartUpdated);
        document.addEventListener('ssp:cart:update', handleCartUpdated);
    }
    
    // Listen for Shopify pub/sub events immediately if available
    // Also set up a delayed check in case subscribe isn't ready yet
    let pubSubSetup = false;
    const setupPubSub = () => {
        if (pubSubSetup) return true; // Already set up
        
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const eventName = PUB_SUB_EVENTS.cartUpdate || 'cart-update';
            subscribe(eventName, (event) => {
                // If event has cartData, use it directly for immediate update (no debounce needed)
                if (event && event.cartData) {
                    updateCartItemCount(event.cartData);
                } else {
                    // Otherwise fetch fresh cart with debouncing
                    handleCartUpdated();
                }
            });
            pubSubSetup = true;
            return true;
        }
        return false;
    };
    
    // Try to set up pub/sub immediately
    if (!setupPubSub()) {
        // If not available yet, try again after delays
        const retryDelays = [100, 500, 1000];
        retryDelays.forEach((delay) => {
            setTimeout(() => {
                if (!pubSubSetup) {
                    setupPubSub();
                }
            }, delay);
        });
    }
    
    // Intercept fetch calls to /cart/add.js as a fallback
    if (typeof window !== 'undefined' && window.fetch) {
        const originalFetch = window.fetch;
        window.fetch = function(...args) {
            const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
            const result = originalFetch.apply(this, args);
            
            // If this is a cart add/update/change request, update cart bubble after it completes
            if ((url.includes('/cart/add.js') || url.includes('/cart/update.js') || url.includes('/cart/change.js')) && args[1]?.method === 'POST') {
                result.then(response => {
                    if (response.ok) {
                        // Use debounced handler to prevent multiple rapid updates
                        handleCartUpdated();
                    }
                }).catch(() => {
                    // Ignore errors
                });
            }
            
            return result;
        };
    }
})();

// Function to fetch the current cart with retry logic
async function getCart(retryCount = 3, delay = 1000) {
    if (fetchInProgress) {
        return null;
    }

    try {
        fetchInProgress = true;
        clearTimeout(fetchTimeout);

        for (let i = 0; i < retryCount; i++) {
            try {
                const response = await fetch('/cart.js');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const cartData = await response.json();
                return cartData;
            } catch (error) {
                if (i === retryCount - 1) throw error;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    } catch (error) {
        console.error("Error fetching cart after all retries:", error);
        return null;
    } finally {
        fetchInProgress = false;
        fetchTimeout = setTimeout(() => {
            fetchInProgress = false;
        }, 1000);
    }
}

// Function to handle cart changes
function handleCartChanges(cart) {
    try {
        if (cart && typeof cart === 'object') {
            updateCartItemCount(cart);
        }
    } catch (error) {
        console.error('Error handling cart changes:', error);
    }
}

// Function to compare carts
function isCartChanged(oldCart, newCart) {
    try {
        // Handle null/undefined cases
        if (!oldCart && !newCart) return false;
        if (!oldCart || !newCart) return true;

        // Safely compare cart contents
        return JSON.stringify(oldCart) !== JSON.stringify(newCart);
    } catch (error) {
        console.error('Error comparing carts:', error);
        // If comparison fails, assume carts are different to be safe
        return true;
    }
}

// Modified checkCart function with debouncing
async function checkCart(previousCart) {
    try {
        if (fetchInProgress) return previousCart;

        const currentCart = await getCart();
        if (currentCart && isCartChanged(previousCart, currentCart)) {
            handleCartChanges(currentCart);
            return currentCart;
        }
        return previousCart;
    } catch (error) {
        console.error('Error in checkCart:', error);
        return previousCart;
    }
}

// Function to set up cart change detection
async function setupCartChangeDetection() {
    try {
        let previousCart = await getCart();

        // Set up a MutationObserver to watch for DOM changes (less aggressive)
        const observer = new MutationObserver(async (mutations) => {
            try {
                // Only check cart if mutations affect cart-related elements
                const cartRelated = mutations.some(mutation =>
                    mutation.target && mutation.target.closest &&
                    (mutation.target.closest('.cart-drawer') ||
                     mutation.target.closest('.cart-count-bubble') ||
                     mutation.target.matches('.cart-drawer, .cart-count-bubble'))
                );
                if (cartRelated) {
                    previousCart = await checkCart(previousCart);
                }
            } catch (error) {
                // Silently handle observer errors to prevent site disruption
                console.error('Cart observer error:', error);
            }
        });

        // Safely start observing with error handling
        try {
            if (document.body) {
                observer.observe(document.body, { subtree: true, childList: true, attributes: true });
            }
        } catch (error) {
            console.error('Failed to start cart observer:', error);
        }


        // Initial check on page load
        try {
            previousCart = await checkCart(previousCart);
        } catch (error) {
            console.error('Error during initial cart check:', error);
        }
        
        isInitialized = true;
    } catch (error) {
        console.error('Error setting up cart change detection:', error);
    }
}

// Function to update cart item count
function updateCartItemCount(cart) {
    try {
        // Check if cart is null or doesn't have items
        if (!cart || typeof cart !== 'object') {
            return;
        }

        if (!cart.items || !Array.isArray(cart.items)) {
            return;
        }

        // Use cart.item_count directly to include shipping protection
        // This ensures shipping protection is counted in the cart count
        let itemCount;
        try {
            // Use cart.item_count if available (includes all items including shipping protection)
            // Otherwise calculate from items array
            if (typeof cart.item_count === 'number') {
                itemCount = cart.item_count;
            } else {
                itemCount = cart.items.reduce((count, item) => {
                    const quantity = item && typeof item.quantity === 'number' ? item.quantity : 0;
                    return count + quantity;
                }, 0);
            }
        } catch (error) {
            console.error('Error calculating item count:', error);
            return;
        }

        // Update the DOM with the new item count - update ALL cart bubbles
        try {
            const headerCartIconLinks = document.querySelectorAll('header #cart-icon-bubble, header .header__icon--cart');
            const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
            
            const updateOrCreateBubble = (container, onlyCreateInHeader = false) => {
                let bubble = container.querySelector('.cart-count-bubble');
                
               
                const isInHeader = container.closest('header') !== null;
                const shouldCreate = !bubble && itemCount > 0 && (!onlyCreateInHeader || isInHeader);
                
                if (shouldCreate) {
                    bubble = document.createElement('div');
                    bubble.className = 'cart-count-bubble';
                    container.appendChild(bubble);
                }
                
                if (bubble) {
                    const countSpan = bubble.querySelector('span[aria-hidden="true"]');
                    const visuallyHidden = bubble.querySelector('.visually-hidden');
                    
                    if (itemCount > 0) {
                        if (countSpan) {
                            countSpan.textContent = itemCount;
                        } else {
                            const newSpan = document.createElement('span');
                            newSpan.setAttribute('aria-hidden', 'true');
                            newSpan.textContent = itemCount;
                            bubble.appendChild(newSpan);
                        }
                        
                        if (visuallyHidden) {
                            visuallyHidden.textContent = `Cart count: ${itemCount}`;
                        } else {
                            const newVisuallyHidden = document.createElement('span');
                            newVisuallyHidden.className = 'visually-hidden';
                            newVisuallyHidden.textContent = `Cart count: ${itemCount}`;
                            bubble.appendChild(newVisuallyHidden);
                        }
                        
                        bubble.classList.remove('hidden');
                    } else {
                        bubble.classList.add('hidden');
                    }
                }
            };
            
            cartCountBubbles.forEach(bubble => {
                try {
                    const container = bubble.closest('a') || bubble.parentElement;
                    if (container) {
                        updateOrCreateBubble(container, false);
                    }
                } catch (bubbleError) {
                }
            });
            
            headerCartIconLinks.forEach(link => {
                try {
                    updateOrCreateBubble(link, true);
                } catch (linkError) {
                }
            });
            
           
            if (cartCountBubbles.length === 0 && headerCartIconLinks.length === 0) {
                const retryDelays = [100, 300, 500, 1000, 2000];
                retryDelays.forEach((delay) => {
                    setTimeout(() => {
                        try {
                            const retryLinks = document.querySelectorAll('header #cart-icon-bubble, header .header__icon--cart');
                            retryLinks.forEach(link => {
                                updateOrCreateBubble(link, true);
                            });
                        } catch (retryError) {
                        }
                    }, delay);
                });
            }
        } catch (domError) {
            console.error('Error updating cart count DOM:', domError);
        }
    } catch (error) {
        console.error('Error updating cart item count:', error);
    }
}

async function initializeCartMonitor() {
    try {
        // Initialize cart monitoring
        await setupCartChangeDetection();

        // Initial cart count update with retry
        try {
            let initialCart = null;
            let retries = 5;
            while (retries > 0 && !initialCart) {
                try {
                    initialCart = await getCart(1, 500); // Single retry with shorter delay
                } catch (fetchError) {
                    console.error('Error fetching initial cart:', fetchError);
                }
                retries--;
                if (!initialCart) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }

            if (initialCart) {
                updateCartItemCount(initialCart);
            } else {
                // Try one more time with longer delay
                setTimeout(async () => {
                    try {
                        const fallbackCart = await getCart();
                        if (fallbackCart) {
                            updateCartItemCount(fallbackCart);
                        }
                    } catch (fallbackError) {
                        console.error('Error in fallback cart fetch:', fallbackError);
                    }
                }, 2000);
            }
        } catch (initError) {
            console.error('Error during cart initialization:', initError);
        }
    } catch (error) {
        console.error('Critical error in cart monitor initialization:', error);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartMonitor);
} else {
    initializeCartMonitor();
}